import React, {
  ReactElement,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  TagInputTypes,
  ChangeMethodType,
  ListItemType,
} from "./tag-input.types";
import classnames from "classnames";
import { TagInputCssOverrides } from "./css-overrides";
import TagInputWithAutocomplete from "../tag-input-with-autocomplete/";
import { ListItem } from "./list-item";
import { Term } from "../tag-input-with-autocomplete/tag-input-with-autocomplete.types";
import { useCombinedRefs } from "../utils/useCombinedRefs";
import {
  isOfTermType,
  handleSelection,
  isTargetTagItem,
  useOutsideClick,
} from "./tag-input.util";
import { isOfStringType } from "../tag-input-with-autocomplete/tag-input-with-autocomplete.util";

export const TagInput = forwardRef<HTMLDivElement, TagInputTypes>(
  (
    {
      autocomplete,
      defaultValue,
      ariaLabel = "Remove",
      label,
      name,
      onChange,
      onBlur,
      onFocus,
      placeholder,
      items = [],
      renderItems,
      status,
      statusValue,
      statusMessage,
      value: controlledValue,
      ...rest
    },
    containerExternalRef
  ): ReactElement => {
    // States
    const [uncontrolledTags, setUncontrolledTags] = useState<string[]>(
      defaultValue || []
    );
    // the input itself
    const [value, setValue] = useState<string>("");
    const renderTags = controlledValue || uncontrolledTags;
    const defaultActive = (): boolean => {
      // non empty field (but not focused) displays active state styling
      const val = value.trim();
      return !(val.length === 0 && renderTags?.length === 0);
    };
    // active styles applied to input are neutral color scheme
    const [active, setActive] = useState<boolean>(defaultActive);
    // focus styles applied to input
    const [focus, setFocus] = useState<boolean>(false);
    // hidden styling on input, (collapse width/height) when empty
    const [collapseInput, setCollapseInput] = useState<boolean>(true);
    const [suggestionBoxOpen, setSuggestBoxOpen] = useState<boolean>(false);
    const [tagFocus, _setTagFocus] = useState<number>(renderTags.length);
    const [itemFocus, _setItemFocus] = useState<number>(0);
    const [currentIndex, setCurrentIndex] = useState(0);

    // Refs
    const tagFocusRef = useRef(tagFocus);
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const tagsRef = useRef<HTMLLIElement[]>([]);
    const itemFocusRef = useRef<number>(itemFocus);
    const suggestionBoxRef = useRef<HTMLUListElement>(null);
    const isMounted = useRef<boolean>(false);
    const isRender = useRef<boolean>(true);

    // Custom Hooks
    useOutsideClick(
      () => {
        // reset tagFocus position to the beginning on blur
        setTagFocus(renderTags.length);
      },
      {
        containerRef: containerRef as React.RefObject<HTMLDivElement>,
        suggestionBoxRef: suggestionBoxRef as React.RefObject<HTMLUListElement>,
      }
    );

    useCombinedRefs(containerRef, containerExternalRef);

    // Hook filters suggestion list by search query
    // const filteredList = useFilterByText(items, value);
    const filteredList = useMemo(() => {
      const formatValue = value.toLowerCase();
      if (!isOfStringType(items)) {
        const matchTerms = (items as Term[]).filter((item) => {
          let isMatch = false;
          for (const prop in item) {
            // extracting the value if item.key is a ReactNode
            const extractItem =
              typeof item[prop] === "object"
                ? (item[prop] as any)?.props?.children
                : item[prop];

            if (
              prop !== "metadata" &&
              extractItem.toLowerCase().includes(formatValue)
            )
              isMatch = true;
          }
          return isMatch && item;
        });
        return matchTerms;
      } else {
        return (items as string[]).filter((item: string) => {
          return item.toLowerCase().includes(formatValue);
        }) as string[];
      }
    }, [items, value]);

    // set the active tag index to the keyboard focus
    const setTagFocus = (tagIndex: number): void => {
      tagFocusRef.current = tagIndex;
      _setTagFocus(tagIndex);
    };
    // set the active suggestion list item index to the keyboard focus
    const setItemFocus = (itemIndex: number): void => {
      itemFocusRef.current = itemIndex;
      _setItemFocus(itemIndex);
    };

    /**
     * Sets the input's aria-activedescendant attribute to the current suggestion index,
        which represents a child in the listbox
     */
    const updateActiveDescendant = (index: number): void => {
      if (index >= 0 && index < filteredList.length) {
        inputRef?.current?.setAttribute("aria-activedescendant", `${index}`);
      }
    };

    const handleKeyDownContainer = (
      e: React.KeyboardEvent<HTMLDivElement>
    ): void => {
      const tagsSize = renderTags.length;
      switch (e.key) {
        case "ArrowLeft":
          setTagFocus(tagFocus < 1 ? 0 : tagFocus - 1);
          break;
        case "ArrowRight":
          tagFocus === tagsSize - 1 && inputRef?.current?.focus();
          setTagFocus(tagFocus <= tagsSize - 1 ? tagFocus + 1 : tagFocus);
          break;
        case "ArrowDown":
          setItemFocus(
            itemFocus === filteredList.length - 1 ? 0 : itemFocus + 1
          );
          if (currentIndex < filteredList.length - 1) {
            const newIndex = currentIndex + 1;
            setCurrentIndex(newIndex);
            updateActiveDescendant(newIndex);
          } else {
            // at end of list, set index to beginning
            setCurrentIndex(0);
          }
          break;
        case "ArrowUp":
          setItemFocus(
            itemFocus === 0 ? filteredList.length - 1 : itemFocus - 1
          );
          if (currentIndex > 0) {
            const newIndex = currentIndex - 1;
            setCurrentIndex(newIndex);
            updateActiveDescendant(newIndex);
          } else {
            // at beginning of list, set index to end
            setCurrentIndex(filteredList.length - 1);
          }
          break;
      }
    };

    const handleInputStateStyles = useCallback(
      (isCollapse?: boolean) => {
        const val = value.trim();
        if (val.length < 1 && renderTags?.length < 1) {
          // totally empty
          setFocus(false);
          setActive(false);
          setCollapseInput(true);
        } else if (val.length < 1 && renderTags?.length > 0) {
          // empty input, with tags
          setFocus(false);
          setActive(true);
          setCollapseInput(true);
        } else {
          // entered values and/or with tags
          setFocus(false);
          setActive(true);
          setCollapseInput(isCollapse || false);
        }
      },
      [renderTags?.length, value]
    );

    const renderTagsHandler = useCallback(
      (e: ChangeMethodType, tag: string): void => {
        if (tag.length < 1) return;
        if (controlledValue) {
          onChange?.(e, tag);
          setValue("");
        } else {
          setUncontrolledTags((prevState) => [...prevState, tag]);
          // clear input after every Enter
          setValue("");
        }
      },
      [controlledValue, onChange]
    );

    const onKeyDownInputHandler = (
      e: React.KeyboardEvent<HTMLInputElement>
    ): void => {
      const val = value.trim();
      switch (e.key) {
        // delete last tag if input is empty
        case "Backspace":
          // use untrimmed `value` here so user can backspace input of spaces before removing tags
          value.length === 0 && removeTag(e, renderTags.length - 1);
          break;
        case "Enter":
          // create tag on component and if prop allows on autocomplete
          if (autocomplete) {
            const item = filteredList[currentIndex];
            const trimItem = isOfTermType(item) ? item.value : item;
            onChange?.(e, trimItem);
            setValue("");
            setSuggestBoxOpen(false);
          } else {
            renderTagsHandler(e, val);
          }
          inputRef?.current?.focus();
          isRender.current = false;
          break;
        case "Escape":
          inputRef?.current?.focus();
          setSuggestBoxOpen(false);
      }
    };
    /**
     * Perform several functions like closing list, clearinginput, clean data for filtering when item from list is selected
     */
    const onSelectTerm = (event: ListItemType, item: Term | string): void => {
      handleSelection(event, item, onChange);
      setSuggestBoxOpen?.(false);
      setValue("");
      inputRef?.current?.focus();
      isRender.current = false;
    };

    const onChangeInputHandler = (
      e: React.ChangeEvent<HTMLInputElement>
    ): void => {
      const { value: val } = e.target;
      setValue(val);
      autocomplete && val ? setSuggestBoxOpen(true) : setSuggestBoxOpen(false);
    };

    /**
     * Focus event on the <input> tag
     */
    const onInputFocus = useCallback((): void => {
      setFocus(true);
      setActive(true);
      setCollapseInput(false);
      onFocus?.();
      setTagFocus?.(renderTags.length);
    }, [onFocus, renderTags.length]);

    /**
     * Clicking on the container of the input
     */
    const onClickContainerHandler = (): void => {
      setFocus(true);
      // focus <input> when input container is selected
      inputRef?.current?.focus();
    };

    const onBlurInputHandler = useCallback(
      (e: React.FocusEvent<HTMLInputElement>): void => {
        // the only focusable elements that could be relatedTargets are the wrapping container element,
        // tag, and tag delete button. Otherwise, if null, assumption is the user focuses outside the component
        const target = e.relatedTarget;
        const { current: componentWrapper } = containerRef;
        // field wrapper around input
        const innerComponentWrapper = componentWrapper?.children[0];

        if (
          target === null ||
          (!innerComponentWrapper?.contains(target as Node) &&
            !suggestionBoxRef.current?.contains(target as Node))
        ) {
          // event is outside of component
          const val = value.trim();
          const isCollapseInput = true;

          // create tag on component and, if prop allows, with autocomplete variant
          !autocomplete && renderTagsHandler(e, val);
          // callback provides last tag automatically created, when incomplete value is left in input after blurring
          onBlur?.(val);
          setSuggestBoxOpen(false);
          // func depends on value state and runs before renderTagsHandler has finished updating value state
          // therefore, manually setting the input to collapse on inactive state
          // scenario: (Tag Input NOT Autocomplete) user types inside input, clicks outside component, the component is blurred (active) style
          // but input should collapse so that there is not a blank line under tags on small screen
          // ** warning ** same user behavior on Autocomplete breaks it, when isCollapseInput = true
          handleInputStateStyles(!autocomplete && isCollapseInput);
        } else {
          // event is inside component like a tag, tag remove button, input, container, or suggestions listbox

          if (
            !innerComponentWrapper?.contains(target as Node) &&
            target !== inputRef.current
          ) {
            // target is suggestion item
            handleInputStateStyles();
          } else {
            // target is tag from clicking
            const tags = tagsRef.current;
            // keep focus on input if container or button is clicked
            // (tag itself is not clickable by CSS pointer event,
            // so have to use button element inside tag)
            if (
              innerComponentWrapper === (target as Node) ||
              target.tagName === "BUTTON"
            ) {
              inputRef?.current?.focus();
            }
            // target is tag by keyboard navigation
            if (isTargetTagItem(tags, target)) {
              handleInputStateStyles();
            }
          }
        }
      },
      [value, autocomplete, renderTagsHandler, onBlur, handleInputStateStyles]
    );

    const removeTag = (e: ChangeMethodType, tagIndex: number): void => {
      if (controlledValue?.length) {
        onChange?.(e, "", tagIndex);
      } else {
        setUncontrolledTags((prevState) => {
          return prevState.filter((_, index) => index !== tagIndex);
        });
      }
      // removing tags should always return to focused input,
      // except when tag is removed by controlled variant (outside element)
      inputRef?.current?.focus();
    };

    useEffect(() => {
      if (!isMounted.current) return;
      if (!renderTags.length) {
        const val = value.trim();
        // tag removed by controlled or uncontrolled variant
        // reset tagFocus to zero when all tags have been deleted
        // otherwise tag will gain focus after creation
        val.length === 0 && setActive(false);
        setTagFocus(0);
      } else {
        const val = value.trim();
        // tag added by "controlled" variant
        controlledValue && val.length === 0 && setActive(true);
      }
    }, [controlledValue, renderTags.length, value]);

    useEffect(() => {
      if (!isMounted.current) return;
      if (tagFocusRef.current < renderTags.length) {
        // adding tags
        setTagFocus(renderTags.length);
      } else if (
        tagFocusRef.current > 0 &&
        tagFocusRef.current !== renderTags.length
      ) {
        // removing tags that were not previously focused
        // and don't run when last tag is focused before removing; sets tag focus position equal to length of tags,
        // which is good because its like resetting
        setTagFocus(tagFocusRef.current - 1);
      }
      isRender.current = true;
    }, [controlledValue, renderTags.length, currentIndex]);

    useEffect(() => {
      if (!isMounted.current) {
        isMounted.current = true;
      }
    }, []);

    const statusContainerAttr =
      statusMessage && status ? { [`data-${status}`]: true } : null;

    const containerClasses = classnames("spark-tag-input__container", {
      active: active,
      focus: focus,
      hide: collapseInput,
    });

    return (
      <>
        <TagInputCssOverrides />
        <div
          ref={containerRef}
          className="spark-tag-input"
          onKeyDown={handleKeyDownContainer}
          {...statusContainerAttr}
          {...rest}
        >
          <div
            tabIndex={-1}
            className={containerClasses}
            onClick={onClickContainerHandler}
          >
            <div className="spark-tag-input__input">
              <ul className="spark-tag-input__tags">
                {isRender.current &&
                  renderTags.length > 0 &&
                  renderTags?.map(
                    (tag: string, index: number, array: string[]) => (
                      <ListItem
                        ref={(node: HTMLLIElement): void => {
                          // assign node to tags collection
                          tagsRef.current[index] = node;
                        }}
                        key={`spark-tag-${index + 1}`}
                        tag={tag}
                        status={status}
                        statusValue={statusValue}
                        ariaLabel={ariaLabel}
                        removeTag={removeTag}
                        index={index}
                        isFocus={tagFocus === index}
                      />
                    )
                  )}
                <li>
                  <label htmlFor="spark-filled-tags" className="spark-label">
                    {label}
                  </label>
                  <input
                    id="spark-filled-tags"
                    role="combobox"
                    aria-live="polite"
                    aria-haspopup="listbox"
                    aria-controls="suggestion-listbox_"
                    aria-autocomplete="list"
                    aria-expanded={suggestionBoxOpen}
                    ref={inputRef}
                    type="text"
                    name={name}
                    className="spark-input--tag-input"
                    onChange={onChangeInputHandler}
                    onKeyDown={onKeyDownInputHandler}
                    value={value}
                    placeholder={focus ? placeholder : undefined}
                    onFocus={onInputFocus}
                    onBlur={onBlurInputHandler}
                  />
                </li>
              </ul>
            </div>
          </div>
          {statusMessage && (
            <span className="spark-input__message">{statusMessage}</span>
          )}
        </div>
        {autocomplete && (
          <TagInputWithAutocomplete
            inputValue={value}
            items={filteredList}
            renderItems={renderItems}
            open={suggestionBoxOpen}
            ref={suggestionBoxRef}
            selectedItems={renderTags}
            positionFocus={currentIndex}
            onSelectTerm={onSelectTerm}
          />
        )}
      </>
    );
  }
);

TagInput.displayName = "TagInput";
